import { IconButton, styled } from '@mui/material'
import { useCallback, useState } from 'react'
import { useVersionReportVersionRepository } from '../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { colorPalette } from '../../../../style/colorPalette'
import { Button } from '../../../../components/buttons'
import { intl } from '../../../../../i18n'
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import { UseCurrentVersionReturnValue } from '../../hooks/currentReport'
import AlertDialog from '../../../../components/dialogs/AlertDialog'
import { VersionInfo } from './VersionInfo'
import { VersionReportMenu } from './ReportMenu'
import { DiscussionButton } from './DiscussionButton'
import { ReportMenuButton } from './ReportMenuButton'
import { UseDiscussionPanelReturnValue } from '../../hooks/useDiscussionPanel'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { useUrlQuery } from '../../hooks/useUrlQuery'
import { StalePageInfo } from '../../hooks/editingState'
import { StaleWarningDialog } from './StaleWarningDialog'

type VersionReportHeaderProps = {
  editing: boolean
  startEditing: () => void
  finishEditing: () => void
  staleWarningDialogOpen: boolean
  stalePages: StalePageInfo[]
  proceedEditing: () => void
  proceedWithLatestData: () => void
  closeStaleWarningDialog: () => void
  evaluate?: () => void
  discussionPanel: UseDiscussionPanelReturnValue
  isVersionSelectMenuOpen: boolean
  onReportMenuToggle: () => void
  showLoading: () => void
  hideLoading: () => void
} & Omit<
  UseCurrentVersionReturnValue,
  | 'updatePageTitle'
  | 'updateLeadSentence'
  | 'updatePageContent'
  | 'updatePageConfig'
  | 'updateVersionUuid'
  | 'updateCurrentVersion'
  | 'storedVersionUuid'
  | 'initialized'
>
export const VersionReportHeader = ({
  currentVersion,
  currentPageIndex,
  toPrevPage,
  toNextPage,
  editing,
  startEditing,
  finishEditing,
  staleWarningDialogOpen,
  stalePages,
  proceedEditing,
  proceedWithLatestData,
  closeStaleWarningDialog,
  evaluate,
  discussionPanel,
  isVersionSelectMenuOpen,
  onReportMenuToggle,
  reCalculateAndFetchVersion,
  showLoading,
  hideLoading,
}: VersionReportHeaderProps) => {
  const { deleteVersion } = useVersionReportVersionRepository()
  const { toUrlWithVersionUuid } = useUrlQuery()
  const deleteCurrentVersion = useCallback(async () => {
    if (!currentVersion) return
    await deleteVersion(currentVersion.uuid)
  }, [currentVersion, deleteVersion])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const openDeleteDialog = useCallback(() => setDeleteDialogOpen(true), [])
  const closeDeleteDialog = useCallback(() => setDeleteDialogOpen(false), [])
  const [recalculateDialogOpen, openRecalculateDialog, closeRecalculateDialog] =
    useDialogState()
  const onDelete = useCallback(async () => {
    await deleteCurrentVersion()
    closeDeleteDialog()
  }, [closeDeleteDialog, deleteCurrentVersion])
  const onReCalculate = useCallback(async () => {
    closeRecalculateDialog()
    showLoading()
    await reCalculateAndFetchVersion()
    hideLoading()
  }, [reCalculateAndFetchVersion, closeRecalculateDialog])
  const onCopyUrl = useCallback(() => {
    if (!currentVersion) return
    const url = toUrlWithVersionUuid(currentVersion.uuid)
    navigator.clipboard.writeText(url)
  }, [toUrlWithVersionUuid, currentVersion])
  return (
    <>
      <HeaderRoot>
        <LeftSideArea>
          <ReportMenuButton
            isOpen={isVersionSelectMenuOpen}
            onToggle={onReportMenuToggle}
          />
          {currentVersion && (
            <VersionTitle>
              {currentVersion.templateName} / {currentVersion.name}
            </VersionTitle>
          )}
          <PagingComponent
            current={currentPageIndex + 1}
            total={currentVersion?.pages.length || 0}
            toPrev={toPrevPage}
            toNext={toNextPage}
          />
        </LeftSideArea>
        {currentVersion && (
          <RightSideArea>
            <DiscussionButton
              isOpen={discussionPanel.isOpen}
              onToggle={discussionPanel.toggle}
            />
            <VersionInfo version={currentVersion} />
            <VersionReportMenu
              onDelete={openDeleteDialog}
              onReCalculate={openRecalculateDialog}
              onCopyUrl={onCopyUrl}
            />
            {!editing && (
              <Button onClick={startEditing}>
                <EditIcon />
                {intl.formatMessage({ id: 'versionReport.edit' })}
              </Button>
            )}
            {editing && (
              <>
                {evaluate && (
                  <Button onClick={evaluate} colorPattern="skyBlue">
                    {intl.formatMessage({
                      id: 'versionReport.generateQualitativeInfo',
                    })}
                  </Button>
                )}
                <Button onClick={finishEditing} colorPattern="green">
                  <DoneIcon />
                  {intl.formatMessage({ id: 'versionReport.finishEditing' })}
                </Button>
              </>
            )}
          </RightSideArea>
        )}
      </HeaderRoot>
      <AlertDialog
        isOpen={deleteDialogOpen}
        title={intl.formatMessage({ id: 'versionReport.delete.title' })}
        message={intl.formatMessage({ id: 'versionReport.delete.message' })}
        submitHandler={onDelete}
        closeHandler={closeDeleteDialog}
      />
      <AlertDialog
        isOpen={recalculateDialogOpen}
        title={intl.formatMessage({ id: 'versionReport.recalculate.title' })}
        message={intl.formatMessage({
          id: 'versionReport.recalculate.message',
        })}
        submitHandler={onReCalculate}
        closeHandler={closeRecalculateDialog}
      />
      <StaleWarningDialog
        isOpen={staleWarningDialogOpen}
        stalePages={stalePages}
        onProceedWithCurrentData={proceedEditing}
        onProceedWithLatestData={proceedWithLatestData}
        onClose={closeStaleWarningDialog}
      />
    </>
  )
}

const HeaderRoot = styled('div')({
  display: 'flex',
  backgroundColor: colorPalette.monotone[0],
  borderRadius: '4px',
  padding: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
})
const LeftSideArea = styled('div')({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
})
const RightSideArea = styled('div')({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
})
const EditIcon = styled(EditRoundedIcon)({
  color: colorPalette.monotone[0],
  fontSize: '16px',
})
const DoneIcon = styled(DoneRoundedIcon)({
  color: colorPalette.monotone[0],
  fontSize: '16px',
})

type PagingComponentProps = {
  current: number
  total: number
  toPrev: () => void
  toNext: () => void
}
const PagingComponent = ({
  current,
  total,
  toPrev,
  toNext,
}: PagingComponentProps) => {
  return (
    <PagingComponentRoot>
      <StyledIconButton onClick={toPrev}>
        <PrevIcon />
      </StyledIconButton>
      <PageText>
        {current}/{total}
      </PageText>
      <StyledIconButton onClick={toNext}>
        <NextIcon />
      </StyledIconButton>
    </PagingComponentRoot>
  )
}

const PagingComponentRoot = styled('div')({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
})

const PrevIcon = styled(NavigateBeforeRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
})
const NextIcon = styled(NavigateNextRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
})
const StyledIconButton = styled(IconButton)({
  padding: '2px',
})
const PageText = styled('span')({
  color: colorPalette.monotone[9],
  fontSize: '12px',
})

const VersionTitle = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '14px',
  fontWeight: 500,
})
